import { useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { fromAuth, useAppDispatch } from '../../../store';
import { useNavigate, useQuery } from '../../../routes';
import { useAccount } from '../../../account';
import { useConfig } from '../../../config';
import { getUserAvatar } from '../../../utils/Users';
import { getFullNameUser } from '../../../models';
import { accountSelection } from '../../routes';
import { ChevronIcon, LogoutIcon, MailIcon, SwitchIcon } from './SidebarIcons';

interface UserMenuProps {
  open: boolean;
  expandSidebar: () => void;
}

const getInitials = (fullName: string) => fullName
  .split(' ')
  .filter(Boolean)
  .slice(0, 2)
  .map(word => word[0])
  .join('')
  .toUpperCase();

const submenuItemSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '9px',
  width: '100%',
  padding: '9px 10px',
  borderRadius: '8px',
  fontSize: 13,
  fontWeight: 600,
  textAlign: 'left',
} as const;

const UserMenu = ({ open, expandSidebar } : UserMenuProps) => {
  const dispatch = useAppDispatch();
  const [expanded, setExpanded] = useState(false);
  const { config } = useConfig();
  const { get } = useQuery();
  const accountId = get('accountId');
  const user = useSelector(fromAuth.selectUser);
  const { accounts } = useAccount();
  const { navigate, replaceQuery } = useNavigate();
  const fullName = getFullNameUser(user?.firstName, user?.lastName, user?.email);

  const handleToggle = () => {
    if (!open) {
      expandSidebar();
      setExpanded(true);
      return;
    }
    setExpanded(!expanded);
  };

  const handleSwitchAccount = () => {
    setExpanded(false);
    navigate(accountSelection.path, replaceQuery({}));
  };

  const handleLogout = () => dispatch(fromAuth.doLogout({
    redirectUrl: `${window.location.origin}/logout`,
  }));

  return (
    <Box id="sidebar-user-menu" sx={{ padding: open ? '0 14px' : '0 10px', marginBottom: '12px' }}>
      {/* Profile card */}
      <ButtonBase
        id="sidebar-user-info"
        onClick={handleToggle}
        title={fullName}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'flex-start' : 'center',
          gap: '11px',
          width: '100%',
          padding: '8px 10px',
          borderRadius: '12px',
          border: '1px solid #eef2f0',
          backgroundColor: '#fff',
          textAlign: 'left',
          '&:hover': {
            backgroundColor: '#f6f8f7',
            borderColor: '#cfe6df',
          },
        }}
      >
        <Avatar
          id="sidebar-user-avatar"
          // No src fallback to the generic default-avatar image: without a
          // real user photo the initials on green must show, per the design.
          src={user?.avatar ? getUserAvatar(user.avatar, config?.publicUrl) : undefined}
          sx={{
            width: 34,
            height: 34,
            backgroundColor: '#0f8461',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '.02em',
            flex: 'none',
          }}
        >
          {getInitials(fullName)}
        </Avatar>
        {open && (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: '#16141d',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <FormattedMessage id="layout.sidebar.user.name" values={{ userName: fullName }} />
              </Typography>
              <Typography
                sx={{
                  fontSize: 11.5,
                  color: '#5b6661',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <FormattedMessage id="layout.sidebar.user.account" values={{ accountId }} />
              </Typography>
            </Box>
            <Box
              id="expand-icon"
              sx={{
                display: 'inline-flex',
                color: '#5b6661',
                transform: `rotate(${expanded ? 180 : 0}deg)`,
                transition: 'transform .2s',
              }}
            >
              <ChevronIcon />
            </Box>
          </>
        )}
      </ButtonBase>

      {/* Inline submenu: email / switch account / log out */}
      {open && expanded && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            padding: '8px',
            margin: '6px 0 0',
            backgroundColor: '#eef2f0',
            border: '1px solid #eef2f0',
            borderRadius: '12px',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 10px 9px' }}>
            <Box sx={{ display: 'inline-flex', color: '#5b6661', flex: 'none' }}>
              <MailIcon />
            </Box>
            <Typography
              sx={{
                fontSize: 12,
                color: '#5f5d75',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.email}
            </Typography>
          </Box>
          {accounts.length > 1 && (
            <ButtonBase
              id="sidebar-user-menu-switch-account"
              onClick={handleSwitchAccount}
              sx={{
                ...submenuItemSx,
                color: '#3c3a50',
                '&:hover': { backgroundColor: '#e3e9e6' },
              }}
            >
              <Box sx={{ display: 'inline-flex', color: '#5f5d75' }}>
                <SwitchIcon />
              </Box>
              <FormattedMessage id="layout.sidebar.user.switch.account.label" />
            </ButtonBase>
          )}
          <ButtonBase
            id="sidebar-user-menu-logout"
            onClick={handleLogout}
            sx={{
              ...submenuItemSx,
              color: '#d0342c',
              '&:hover': { backgroundColor: '#fdecea' },
            }}
          >
            <Box sx={{ display: 'inline-flex', color: '#d0342c' }}>
              <LogoutIcon />
            </Box>
            <FormattedMessage id="layout.sidebar.user.logout" />
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

export default UserMenu;
